import { flexRender, Row } from '@tanstack/react-table';
import clsx from 'clsx';
import { Dispatch, SetStateAction, useState } from 'react';
import { Skeleton } from '../Skeleton';
import { TableBody, TableCell, TableRow } from '../Table';
import { END_ACTIONS_COLUMN_ID } from './DataTable.constants';
import { ExtendedRowData } from './DataTable.types';
import { getSizeStyles } from './DataTable.utils';

const TABLE_CELL_PADDING_CLASSES = 'px-3 py-2';

interface DataTableBodyProps<TData> {
  activeRowIndices: number[];
  colSpan: number;
  rows: Row<TData & ExtendedRowData>[];
  isLoading?: boolean;
  setOpenEndActionsIndex: Dispatch<SetStateAction<number | null>>;
}

const DataTableBody = <TData,>({
  activeRowIndices,
  colSpan,
  rows,
  isLoading,
  setOpenEndActionsIndex,
}: DataTableBodyProps<TData>) => {
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={colSpan} className={TABLE_CELL_PADDING_CLASSES}>
            <Skeleton />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  if (!rows.length) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={colSpan} className={TABLE_CELL_PADDING_CLASSES}>
            No results.
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {rows.map((row, index) => {
        const { disabled, tooltip, onClick } = row.original;
        const isActive = activeRowIndices.includes(index);
        const isHovered = hoveredRowIndex === index;
        return (
          <TableRow
            key={row.id}
            data-state={row.getIsSelected() && 'selected'}
            active={isActive}
            disabled={disabled}
            tooltip={tooltip}
            onClick={onClick}
            onHover={(hover) => setHoveredRowIndex(hover ? index : null)}
            onRightClick={() => setOpenEndActionsIndex(index)}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                className={clsx(
                  TABLE_CELL_PADDING_CLASSES,
                  disabled && cell.column.id !== END_ACTIONS_COLUMN_ID && 'opacity-40'
                )}
                style={getSizeStyles(cell.column.getSize())}
              >
                {flexRender(cell.column.columnDef.cell, { ...cell.getContext(), isActive, isHovered })}
              </TableCell>
            ))}
          </TableRow>
        );
      })}
    </TableBody>
  );
};

export default DataTableBody;
